import React, { useEffect, useMemo, useState } from "react";
import { Col, Form, Modal, Row } from "react-bootstrap";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GetData from "../../../services/getData";
import BlockTitle from "../../../components/layout/blockTitle/BlockTitle";
import TiptapEditor from "../../../components/layout/tiptapEditor/TiptapEditor";
import TiptapViewer from "../../../components/layout/tiptapViewer/TiptapViewer";
import DateInput from "../../../components/ui/DateInput";
import {
  buildUsiFormDefaults,
  getUsiFormSchema,
  getUsiTypeParam,
  isUsiConclusionBlock,
  isUsiRecommendationsBlock,
} from "./usiFormSchema";
import { usiText } from "./usiLabels";
import "../patients.css";
import "./PatientProfile.css";
import "./PatientUziForm.css";

const displayLinedValue = (value) => (!value && value !== 0 ? "" : value);

const looksLikeHtml = (value) =>
  /<\/?[a-z][a-z0-9]*\b[^>]*>/i.test(String(value ?? ""));

const toRichHtml = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  if (looksLikeHtml(raw)) return raw;
  const escaped = raw
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return escaped.replace(/\r?\n/g, "<br />");
};

const formatDate = (value) => {
  const raw = String(value ?? "").trim();
  const isoDate = raw.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return displayLinedValue(value);
  const [year, month, day] = isoDate.split("-");
  return `${day}.${month}.${year}`;
};

const parseDocData = (raw) => {
  if (!raw) return {};
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return {};
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch (_) {
      return {};
    }
  }
  return {};
};

const doctorNameOnly = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const comma = raw.indexOf(",");
  return comma === -1 ? raw : raw.slice(0, comma).trim();
};

const formatDoctorLabel = (doctor) =>
  String(doctor?.name ?? doctor?.name?.value ?? "").trim();

const normalizeDoctorsList = (raw) => {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const list = [];
  raw.forEach((item) => {
    const label = formatDoctorLabel(item);
    if (!label || seen.has(label)) return;
    seen.add(label);
    list.push({
      id: String(item?.id ?? item?.id?.value ?? label),
      label,
    });
  });
  return list;
};

const findDoctorById = (doctorsList, userId) => {
  const id = String(userId ?? "").trim();
  if (!id || id === "0") return null;
  return doctorsList.find((doctor) => String(doctor.id) === id) || null;
};

function FieldControl({ item, value, isEditing, onChange, t }) {
  const label = usiText(item, "label", t);
  const placeholder = usiText(item, "placeholder", t);
  const stacked = item.type === "textarea" || label.length > 42;
  const fieldClass = stacked
    ? "patient-inline-field patient-inline-field--stack"
    : "patient-inline-field";

  return (
    <Row className="mb-3">
      <Col md={12}>
        <div className="patient-form-group">
          <Form.Group controlId={`puf-${item.name}`}>
            <div className={fieldClass}>
              {!item.hideLabel && label ? (
                <Form.Label className="patient-inline-label">
                  {label}:
                </Form.Label>
              ) : null}
              {isEditing ? (
                item.type === "select" ? (
                  <Form.Select
                    className="patient-inline-value"
                    name={item.name}
                    value={value ?? ""}
                    onChange={onChange}>
                    {(item.options || []).map((option) => (
                      <option key={option.value} value={option.value}>
                        {usiText(option, "label", t)}
                      </option>
                    ))}
                  </Form.Select>
                ) : item.type === "textarea" ? (
                  <Form.Control
                    as="textarea"
                    rows={item.rows || 4}
                    className="patient-inline-value"
                    name={item.name}
                    value={value ?? ""}
                    onChange={onChange}
                    placeholder={placeholder || " "}
                  />
                ) : item.inputType === "date" ? (
                  <DateInput
                    className="form-control patient-inline-value"
                    name={item.name}
                    value={value ?? ""}
                    onChange={onChange}
                  />
                ) : (
                  <Form.Control
                    type={item.inputType || "text"}
                    className="patient-inline-value"
                    name={item.name}
                    value={value ?? ""}
                    onChange={onChange}
                    placeholder={placeholder || " "}
                  />
                )
              ) : (
                <div
                  className={
                    item.type === "textarea"
                      ? "patient-inline-value patient-inline-value--multiline"
                      : "patient-inline-value"
                  }>
                  {item.inputType === "date"
                    ? formatDate(value)
                    : displayLinedValue(value)}
                </div>
              )}
            </div>
          </Form.Group>
        </div>
      </Col>
    </Row>
  );
}

function DataTable({ block, formData, isEditing, onChange, t }) {
  const columns = block.columns || [];
  const title = usiText(block, "title", t);
  return (
    <div className="patient-life-anamnesis-block">
      {title ? (
        <Row className="mb-2">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-profile-group-title">{title}</div>
            </div>
          </Col>
        </Row>
      ) : null}
      <div
        className={`puf-table-wrap${columns.length > 4 ? " puf-table-wrap--wide" : ""}`}>
        <table className="puf-spectral-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key}>{usiText(col, "label", t)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(block.rows || []).map((row) => (
              <tr key={row.id}>
                {columns.map((col) => {
                  if (col.readOnly) {
                    return (
                      <td key={col.key}>{usiText(row, col.key, t)}</td>
                    );
                  }
                  const name = `${row.id}_${col.key}`;
                  return (
                    <td key={col.key}>
                      {isEditing ? (
                        <Form.Control
                          name={name}
                          value={formData[name] ?? ""}
                          onChange={onChange}
                          placeholder=" "
                        />
                      ) : (
                        displayLinedValue(formData[name])
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SpectralTable({ block, formData, isEditing, onChange, t }) {
  const title = usiText(block, "title", t);
  return (
    <div className="patient-life-anamnesis-block">
      {title ? (
        <Row className="mb-2">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-profile-group-title">{title}</div>
            </div>
          </Col>
        </Row>
      ) : null}
      <div className="puf-table-wrap">
        <table className="puf-spectral-table">
          <thead>
            <tr>
              <th>{t("usi.lbl.arteriya")}</th>
              <th>{t("usi.lbl.storona")}</th>
              <th>{t("usi.lbl.psv_sm_s")}</th>
              <th>{t("usi.lbl.edv_sm_s")}</th>
              <th>{usiText(block, "spectrumLabel", t) || t("usi.lbl.spektr")}</th>
            </tr>
          </thead>
          <tbody>
            {(block.rows || []).map((row) => (
              <tr key={row.id}>
                <td>{usiText(row, "artery", t)}</td>
                <td>{usiText(row, "side", t)}</td>
                {[row.psv, row.edv, row.spectrum].map((name) => (
                  <td key={name}>
                    {isEditing ? (
                      <Form.Control
                        name={name}
                        value={formData[name] ?? ""}
                        onChange={onChange}
                        placeholder=" "
                      />
                    ) : (
                      displayLinedValue(formData[name])
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PatientUziForm() {
  const { t } = useTranslation("patient");
  const navigate = useNavigate();
  const { patientId, diagnosticsId } = useParams();
  const [searchParams] = useSearchParams();
  const typeFromQuery = String(searchParams.get("type") ?? "").trim();
  const modeFromQuery = String(searchParams.get("mode") ?? "")
    .trim()
    .toLowerCase();
  // Переход из списка диагностик (?mode=view) всегда открывает просмотр,
  // редактирование включается только кнопкой «Редактировать».
  const forceViewMode = modeFromQuery === "view";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [usiType, setUsiType] = useState(typeFromQuery);
  const [isPersisted, setIsPersisted] = useState(false);
  const [isEditing, setIsEditing] = useState(true);
  const [saving, setSaving] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [formData, setFormData] = useState(() =>
    buildUsiFormDefaults(getUsiFormSchema(typeFromQuery)),
  );
  const [savedFormData, setSavedFormData] = useState(() =>
    buildUsiFormDefaults(getUsiFormSchema(typeFromQuery)),
  );
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  const schema = useMemo(() => getUsiFormSchema(usiType), [usiType]);
  const title =
    usiText(schema, "heading", t) ||
    usiText(schema, "title", t) ||
    usiType ||
    t("usi.fallbackTitle");

  const resultBlocks = useMemo(
    () =>
      (schema?.blocks || []).filter(
        (block) =>
          !isUsiConclusionBlock(block) && !isUsiRecommendationsBlock(block),
      ),
    [schema],
  );

  const doctorOptions = useMemo(() => {
    const list = [...doctors];
    const current = doctorNameOnly(formData.doctor);
    if (current && !list.some((d) => d.label === current)) {
      list.unshift({
        id: formData.doctor_id || `custom:${current}`,
        label: current,
      });
    }
    return list;
  }, [doctors, formData.doctor, formData.doctor_id]);

  const showDoctorSelect =
    isEditing && (isPersisted || !String(formData.doctor || "").trim());

  useEffect(() => {
    let cancelled = false;

    const loadProtocol = async () => {
      if (!diagnosticsId) {
        setLoading(false);
        setError(t("usi.notSpecified"));
        return;
      }

      setLoading(true);
      setError("");
      try {
        const [viewRes, patientInfo] = await Promise.all([
          GetData({
            module: "patient",
            block: "diagnostics",
            action: "view",
            id_patient: patientId,
            id: diagnosticsId,
          }),
          GetData({
            module: "patient",
            block: "info",
            id_patient: patientId,
          }),
        ]);

        if (cancelled) return;

        if (viewRes instanceof Error || viewRes?.status?.success === false) {
          throw new Error(
            viewRes?.status?.message ||
              viewRes?.message ||
              t("usi.loadFailedShort"),
          );
        }

        const row =
          viewRes?.result && !Array.isArray(viewRes.result)
            ? viewRes.result
            : Array.isArray(viewRes?.result)
              ? viewRes.result[0]
              : null;

        if (!row?.id) {
          throw new Error(t("usi.notFound"));
        }

        const typeName = getUsiTypeParam(row.name) || typeFromQuery;
        const nextSchema = getUsiFormSchema(typeName);
        const defaults = buildUsiFormDefaults(nextSchema);
        const content = parseDocData(row.content);
        const hasContent = Object.keys(content).some(
          (key) => String(content[key] ?? "").trim() !== "",
        );
        const mapped = { ...defaults, ...content };
        mapped.doctor = doctorNameOnly(mapped.doctor);
        const doctorsList = normalizeDoctorsList(
          patientInfo?.result?.all_doctors || patientInfo?.all_doctors || [],
        );

        if (!String(mapped.doctor || "").trim()) {
          const creator = findDoctorById(doctorsList, row.created_user_id);
          if (creator?.label) {
            mapped.doctor = doctorNameOnly(creator.label);
            mapped.doctor_id = creator.id;
          }
        }

        if (row.date && !mapped.study_date) {
          mapped.study_date = String(row.date).slice(0, 10);
        }

        setUsiType(typeName || row.name || "");
        setDoctors(doctorsList);
        setFormData(mapped);
        setSavedFormData(mapped);
        setIsPersisted(hasContent);
        setIsEditing(forceViewMode ? false : !hasContent);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || t("usi.loadFailed"));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadProtocol();
    return () => {
      cancelled = true;
    };
  }, [patientId, diagnosticsId, typeFromQuery, forceViewMode, t]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => {
      if (name === "doctor") {
        const selected = doctorOptions.find((d) => d.label === value);
        return {
          ...prev,
          doctor: value,
          doctor_id:
            selected?.id && !String(selected.id).startsWith("custom:")
              ? selected.id
              : prev.doctor_id,
        };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleRecommendationsChange = (html) => {
    setFormData((prev) => ({ ...prev, recommendations: html }));
  };

  const handleSave = async () => {
    if (!diagnosticsId || saving) return;

    const payload = {
      ...formData,
      usi_type: schema?.key || usiType || "",
    };

    setSaving(true);
    try {
      const result = await GetData({
        module: "patient",
        block: "diagnostics",
        action: "update",
        id_patient: patientId,
        id: diagnosticsId,
        date: formData.study_date || "",
        content: payload,
      });

      if (result instanceof Error || result?.status?.success === false) {
        throw new Error(
          result?.status?.message ||
            result?.message ||
            t("usi.saveFailedShort"),
        );
      }

      const savedContent = parseDocData(result?.result?.content);
      const mapped = {
        ...payload,
        ...(Object.keys(savedContent).length ? savedContent : {}),
      };
      setFormData(mapped);
      setSavedFormData(mapped);
      setIsPersisted(true);
      setIsEditing(false);
      setModalMessage(result?.status?.message || t("usi.saved"));
      setShowModal(true);
    } catch (err) {
      console.error("Ошибка сохранения УЗИ:", err);
      setModalMessage(err.message || t("usi.saveFailed"));
      setShowModal(true);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData(savedFormData);
    setIsEditing(false);
  };

  const handlePrint = () => {
    if (!patientId || !diagnosticsId) return;
    navigate(`/patient/${patientId}/diagnostics/uzi/${diagnosticsId}/print`);
  };

  const handleDelete = async () => {
    if (!diagnosticsId) return;
    if (!window.confirm(t("usi.confirmDelete"))) return;
    try {
      const result = await GetData({
        module: "patient",
        block: "diagnostics",
        action: "delete",
        id_patient: patientId,
        id: diagnosticsId,
      });

      if (result instanceof Error || result?.status?.success === false) {
        throw new Error(
          result?.status?.message ||
            result?.message ||
            t("usi.deleteFailed"),
        );
      }

      backToList();
    } catch (err) {
      console.error("Ошибка удаления УЗИ:", err);
      setModalMessage(err.message || t("usi.deleteFailed"));
      setShowModal(true);
    }
  };

  const backToList = () => navigate(`/patient/${patientId}/diagnostics`);

  const printButton = {
    label: (
      <span
        style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
        <svg
          viewBox="0 0 24 24"
          aria-hidden="true"
          width="20"
          height="20"
          style={{
            fill: "currentColor",
            transform: "scale(1.2)",
            transformOrigin: "center",
          }}>
          <path d="M19 8H5a3 3 0 0 0-3 3v4h4v4h12v-4h4v-4a3 3 0 0 0-3-3zm-3 9H8v-5h8v5zm3-7a1 1 0 1 1 0 2 1 1 0 0 1 0-2zM18 3H6v4h12V3z" />
        </svg>
        {t("docs.buttons.print")}
      </span>
    ),
    onClick: handlePrint,
    type: "light icon",
  };

  if (loading) return <div className="loading">{t("plan.loading")}</div>;
  if (error) {
    return (
      <div className="puf-empty">
        {error}{" "}
        <button
          type="button"
          className="button-default light"
          onClick={backToList}>
          {t("usi.backToList")}
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="patient-name-title-sticky">
        <BlockTitle
          title={title}
          buttons={
            isEditing
              ? [
                  {
                    label: saving ? t("usi.saving") : t("docs.buttons.save"),
                    onClick: handleSave,
                    type: "normal",
                  },
                  {
                    label: t("docs.buttons.cancel"),
                    onClick: handleCancel,
                    type: "light",
                  },
                ]
              : [
                  {
                    label: (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                        }}>
                        <svg
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                          width="20"
                          height="20"
                          style={{ fill: "currentColor" }}>
                          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm2.92 2.33H5v-.92l9.06-9.06.92.92L5.92 19.58zM20.71 7.04a1.003 1.003 0 0 0 0-1.42L18.37 3.29a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.83z" />
                        </svg>
                        {t("docs.buttons.edit")}
                      </span>
                    ),
                    onClick: () => setIsEditing(true),
                    type: "light icon",
                  },
                  printButton,
                  {
                    label: (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                        }}>
                        <svg
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                          width="20"
                          height="20"
                          style={{ fill: "currentColor" }}>
                          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                        </svg>
                        {t("docs.buttons.delete")}
                      </span>
                    ),
                    onClick: handleDelete,
                    type: "danger icon",
                  },
                  {
                    label: (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                        }}>
                        <svg
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                          width="20"
                          height="20"
                          style={{ color: "currentColor" }}>
                          <path
                            d="M9 14L4 9l5-5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M4 9h11a4 4 0 1 1 0 8H9"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        {t("docs.buttons.backToList")}
                      </span>
                    ),
                    onClick: backToList,
                    type: "normal icon",
                  },
                ]
          }
        />
      </div>

      <Form
        className={`puf-form mt-4${isEditing ? " patient-profile--editing" : ""}`}>
        {!schema ? (
          <div className="puf-unsupported">
            {t("usi.unsupported", { type: title })}
          </div>
        ) : null}

        <div className="patient-life-anamnesis-block">
          <FieldControl
            item={{
              name: "study_date",
              labelKey: "usi.studyDate",
              inputType: "date",
            }}
            value={formData.study_date}
            isEditing={isEditing}
            onChange={handleChange}
            t={t}
          />
          {(schema?.headerItems || []).map((item) => (
            <FieldControl
              key={item.name}
              item={item}
              value={formData[item.name]}
              isEditing={isEditing}
              onChange={handleChange}
              t={t}
            />
          ))}
        </div>

        {(resultBlocks || []).map((block, blockIndex) =>
          block.type === "spectral_table" ? (
            <SpectralTable
              key={block.title || `spectral-${blockIndex}`}
              block={block}
              formData={formData}
              isEditing={isEditing}
              onChange={handleChange}
              t={t}
            />
          ) : block.type === "table" ? (
            <DataTable
              key={block.title || `table-${blockIndex}`}
              block={block}
              formData={formData}
              isEditing={isEditing}
              onChange={handleChange}
              t={t}
            />
          ) : (
            <div
              className="patient-life-anamnesis-block"
              key={block.title || `puf-block-${blockIndex}`}>
              {block.title ? (
                <Row className="mb-2">
                  <Col md={12}>
                    <div className="patient-form-group">
                      <div className="patient-profile-group-title">
                        {usiText(block, "title", t)}
                      </div>
                    </div>
                  </Col>
                </Row>
              ) : null}
              {(block.items || []).map((item) => (
                <FieldControl
                  key={item.name}
                  item={item}
                  value={formData[item.name]}
                  isEditing={isEditing}
                  onChange={handleChange}
                  t={t}
                />
              ))}
            </div>
          ),
        )}

        <div className="patient-life-anamnesis-block">
          <Row className="mb-2">
            <Col md={12}>
              <div className="patient-form-group">
                <div className="patient-profile-group-title">
                  {t("usi.sectionConclusion")}
                </div>
              </div>
            </Col>
          </Row>
          <FieldControl
            item={{
              name: "conclusion",
              type: "textarea",
              hideLabel: true,
              rows: 5,
            }}
            value={formData.conclusion}
            isEditing={isEditing}
            onChange={handleChange}
            t={t}
          />
        </div>

        <div className="patient-life-anamnesis-block">
          <Row className="mb-2">
            <Col md={12}>
              <div className="patient-form-group">
                <div className="patient-profile-group-title">
                  {t("usi.sectionRecommendations")}
                </div>
              </div>
            </Col>
          </Row>
          {isEditing ? (
            <TiptapEditor
              value={formData.recommendations || ""}
              onChange={handleRecommendationsChange}
              placeholder={t("usi.recommendationsPlaceholder")}
              minHeight="200px"
            />
          ) : (
            <TiptapViewer
              content={toRichHtml(formData.recommendations)}
              emptyText={t("usi.recommendationsEmpty")}
            />
          )}
        </div>

        <div className="patient-life-anamnesis-block">
          <Row className="mb-2">
            <Col md={12}>
              <div className="patient-form-group">
                <div className="patient-profile-group-title">
                  {t("usi.sectionFollowup")}
                </div>
              </div>
            </Col>
          </Row>
          <FieldControl
            item={{
              name: "followup_term",
              labelKey: "usi.followupTermShort",
              type: "textarea",
              rows: 2,
            }}
            value={formData.followup_term}
            isEditing={isEditing}
            onChange={handleChange}
            t={t}
          />
          <FieldControl
            item={{
              name: "followup_purpose",
              labelKey: "usi.followupPurpose",
              type: "textarea",
              rows: 2,
            }}
            value={formData.followup_purpose}
            isEditing={isEditing}
            onChange={handleChange}
            t={t}
          />
        </div>

        <div className="patient-life-anamnesis-block">
          <Row className="mb-2">
            <Col md={12}>
              <div className="patient-form-group">
                <div className="patient-profile-group-title">
                  {t("usi.doctor")}
                </div>
              </div>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <Form.Group controlId="puf-doctor">
                  <div className="patient-inline-field">
                    <Form.Label className="patient-inline-label">
                      {t("usi.doctorFull")}
                    </Form.Label>
                    {showDoctorSelect ? (
                      <Form.Select
                        className="patient-inline-value"
                        name="doctor"
                        value={formData.doctor ?? ""}
                        onChange={handleChange}>
                        <option value="">{t("docs.doctorPlaceholder")}</option>
                        {doctorOptions.map((doctor) => (
                          <option key={doctor.id} value={doctor.label}>
                            {doctor.label}
                          </option>
                        ))}
                      </Form.Select>
                    ) : (
                      <div className="patient-inline-value">
                        {displayLinedValue(formData.doctor)}
                      </div>
                    )}
                  </div>
                </Form.Group>
              </div>
            </Col>
          </Row>
        </div>
      </Form>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("docs.errors.alertTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalMessage}</Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="button-default"
            onClick={() => setShowModal(false)}>
            {t("actions.ok", { ns: "common" })}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default PatientUziForm;
